import React, { useState, useEffect } from 'react';
import { Check, ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySwitcher from '../components/CurrencySwitcher';
import { useNavigate } from 'react-router-dom';

export default function Checkout() {
  const { user } = useAuth();
  const { currency, getPrice, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('institutional');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [tradingViewId, setTradingViewId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill TradingView ID if user is logged in
  useEffect(() => {
    if (user) setTradingViewId(user.tradingViewId || '');
  }, [user]);

  const plans = {
    institutional: {
      name: 'HDALGOS INSTITUTIONAL SUITE',
      cycles: {
        monthly: {
          priceKey: 'monthly',
          billingPeriod: 'month',
          subtitle: 'Flexible Access',
          features: [
            '25+ Institutional Indicators',
            'HDAlgos Pro Engine (Flagship)',
            'All Flagship Systems (Forecast Paint, Trend Exhaustion, Volatility Breakout)',
            'Trade Ideas Sent via Discord',
            'Access to Former Goldman Trader (after 3 months)',
            'Full Access to All Systems & Tools',
            'Priority Support'
          ]
        },
        quarterly: {
          priceKey: 'quarterly',
          billingPeriod: 'quarter',
          subtitle: 'Best Value',
          features: [
            '25+ Institutional Indicators',
            'HDAlgos Pro Engine (Flagship)',
            'All Flagship Systems (Forecast Paint, Trend Exhaustion, Volatility Breakout)',
            'Trade Ideas Sent via Discord',
            'Immediate Access to Former Goldman Trader',
            'Full Access to All Systems & Tools',
            'Priority Support'
          ]
        },
        lifetime: {
          priceKey: 'lifetime',
          billingPeriod: 'lifetime',
          subtitle: 'Ultimate Access',
          features: [
            '25+ Institutional Indicators',
            'HDAlgos Pro Engine (Flagship)',
            'All Flagship Systems (Forecast Paint, Trend Exhaustion, Volatility Breakout)',
            'Trade Ideas Sent via Discord',
            'Immediate Access to Former Goldman Trader',
            'Access to Beta Indicators',
            'Offline Machine Learning Systems',
            'Full Access to All Systems & Tools',
            'Priority Support',
            'Lifetime Updates & Features'
          ]
        }
      }
    }
  };

  const currentPlan = plans[selectedPlan];
  const currentCycle = currentPlan.cycles[billingCycle] || currentPlan.cycles.monthly;
  const price = getPrice(currentCycle.priceKey);
  const currencySymbol = getCurrencySymbol();
  const billingLabel = billingCycle === 'monthly' ? 'month' : billingCycle === 'quarterly' ? 'quarter' : 'lifetime';
  const priceDisplay = `${currencySymbol}${price}${billingCycle === 'lifetime' ? '' : ` / ${billingLabel}`}`;

  // Stripe Price IDs mapping
  const stripePriceIds = {
    institutional_monthly: process.env.REACT_APP_STRIPE_INSTITUTIONAL_MONTHLY_PRICE_ID || 'price_1SbgVoBbueTqw5pCfXEawbCj',
    institutional_quarterly: process.env.REACT_APP_STRIPE_INSTITUTIONAL_QUARTERLY_PRICE_ID || process.env.REACT_APP_STRIPE_INSTITUTIONAL_YEARLY_PRICE_ID || 'price_1ST8QvBbueTqw5pCaiFFd60o',
    institutional_lifetime: process.env.REACT_APP_STRIPE_INSTITUTIONAL_LIFETIME_PRICE_ID || 'price_1SUAAzBbueTqw5pCfqX6RuMn',
  };

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (!tradingViewId) {
      setError('Please enter your TradingView username');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the appropriate price ID
      const priceId = stripePriceIds[`${selectedPlan}_${billingCycle}`];

      console.log('Submitting checkout', { priceId, tradingViewId, plan: selectedPlan, billingCycle });

      // Create a checkout session on your backend
      // For local development, use http://localhost:3001
      // For production, it will automatically use your domain
      const apiUrl = '/api/create-checkout-session';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          tradingViewId,
          plan: selectedPlan,
          billingCycle,
          currency,
        }),
      });

      const session = await response.json();

      if (session.error) {
        console.error('Checkout session error', session.error);
        setError(session.error);
        setLoading(false);
        return;
      }

      if (session.url) {
        window.location.assign(session.url);
        return;
      }

      setError('Unable to redirect to Stripe Checkout. Please contact support.');
      setLoading(false);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err?.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Header */}
      <nav className="border-b border-neutral-800 bg-[#111315]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src="/logo.png" alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <a href="/" className="text-sm text-neutral-400 hover:text-white transition flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Order Summary */}
          <div>
            <h1 className="text-4xl md:text-5xl font-black mb-2">Checkout</h1>
            <p className="text-neutral-400 mb-4">Complete your subscription to HDAlgos</p>

            {/* Currency Switcher (hidden) */}
            <div className="mb-6 hidden">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-3 block">
                Currency
              </label>
              <CurrencySwitcher />
            </div>

            {/* Free Trial Banner */}

            {/* Selected Plan Display */}
            <div className="mb-8">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-4 block">
                Your Plan
              </label>
              <div className="bg-[#CBB26A]/10 ring-2 ring-[#CBB26A] rounded-xl p-5">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-bold text-lg">{currentPlan.name}</div>
                    <div className="text-sm text-[#CBB26A] mt-1">{currentCycle.subtitle}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-2xl">{priceDisplay}</div>
                    <div className="text-xs text-neutral-400">{billingCycle === 'lifetime' ? 'One‑time payment' : `Billed per ${billingLabel}`}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Cycle */}
            <div className="mb-8">
              <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-4 block">
                Billing Cycle
              </label>
              <div className="grid grid-cols-3 gap-2 bg-neutral-900 rounded-lg p-2 ring-1 ring-neutral-800">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-3 rounded-lg font-bold transition text-sm ${
                    billingCycle === 'monthly'
                      ? 'bg-[#CBB26A] text-black'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('quarterly')}
                  className={`px-4 py-3 rounded-lg font-bold transition text-sm relative ${
                    billingCycle === 'quarterly'
                      ? 'bg-[#CBB26A] text-black'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  Quarterly
                  <span className="absolute -top-2 -right-2 bg-green-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">SAVE</span>
                </button>
                <button
                  onClick={() => setBillingCycle('lifetime')}
                  className={`px-4 py-3 rounded-lg font-bold transition text-sm ${
                    billingCycle === 'lifetime'
                      ? 'bg-[#CBB26A] text-black'
                      : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  Lifetime
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-bold mb-4">Order Summary</h3>

              <div className="space-y-3 mb-4 pb-4 border-b border-neutral-800">
                <div className="flex justify-between">
                  <span className="text-neutral-400">{currentPlan.name} - {currentCycle.subtitle}</span>
                  <span className="font-bold text-neutral-200">{priceDisplay}</span>
                </div>
              </div>

              <div className="flex justify-between text-lg font-bold pt-4 border-t border-neutral-800">
                <span>Total{billingCycle === 'lifetime' ? ' (One-time)' : ''}</span>
                <span className="text-[#CBB26A]">{priceDisplay}</span>
              </div>
            </div>

            {/* Features */}
            <div className="bg-neutral-950/50 ring-1 ring-neutral-800/50 rounded-xl p-6">
              <h3 className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-4">
                Included Features
              </h3>
              <div className="space-y-2">
                {currentCycle.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                    <span className={feature.includes('Beta') || feature.includes('Offline') ? 'text-[#CBB26A] font-bold text-sm' : 'text-neutral-400 text-sm'}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div>
            <div className="bg-neutral-950 ring-1 ring-neutral-800 rounded-xl p-8 sticky top-8">
              <h2 className="text-2xl font-black mb-6">Your Details</h2>

              {/* Error Message */}
              {error && (
                <div className="bg-red-950/30 ring-1 ring-red-900/50 rounded-lg p-4 mb-6">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleCheckout}>
                {/* TradingView ID */}
                <div className="mb-6">
                  <label className="text-sm font-bold tracking-wider uppercase text-neutral-400 mb-2 block">
                    TradingView Username
                  </label>
                  <input
                    type="text"
                    placeholder="@yourusername"
                    value={tradingViewId}
                    onChange={(e) => setTradingViewId(e.target.value)}
                    required
                    className="w-full bg-neutral-900 ring-1 ring-neutral-800 rounded-lg px-4 py-3 text-white placeholder-neutral-600 focus:ring-2 focus:ring-[#CBB26A] outline-none transition"
                  />
                  <p className="text-xs text-neutral-500 mt-2">
                    We'll use this to grant you access on TradingView
                  </p>
                </div>

                {/* Note about Stripe handling payment details */}
                <div className="mb-6 bg-[#CBB26A]/10 ring-1 ring-[#CBB26A]/30 rounded-lg p-4">
                  <p className="text-sm text-neutral-300">
                    Payment details are collected by Stripe on the next page.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-4 rounded-lg font-black text-lg hover:from-[#D8C585] hover:to-[#BDA45E] transition transform hover:scale-[1.02] mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Continue to Payment'}
                </button>

                {/* Security Notice */}
                <div className="flex items-center justify-center gap-2 text-xs text-neutral-500">
                  <Lock className="w-3 h-3" />
                  <span>Secure payment processed by Stripe</span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
